// Notes — the notes of a space, on top of its journal.
//
// Local edits go into the note's NoteDoc immediately; its Yjs updates are batched for
// FLUSH_MS (and flushed when the page is hidden) into one 'note.update' envelope. Envelopes from
// other devices are applied to the loaded doc, or to the stored state if the note is not open.
// A plaintext index (title, folder, dates, excerpt) is kept for the list and search, as notd did.
//
// Events: 'change' {id} (a note's content or metadata changed), 'folders'.

import { b64url, fromB64url, randomHex } from '../core/ids';
import { NotMemberError, type Journal } from '../space/journal';
import type { Payload } from '../space/envelope';
import type { Store } from '../space/store';
import { NoteDoc, type NoteFields } from './doc';

const FLUSH_MS = 800;
const SAVE_MS = 400;
const EXCERPT = 160;

export type NoteMeta = NoteFields & { id: string; edited: number; excerpt: string; size: number };
export type Folder = { id: string; name: string; color: string | null; position: number; ts: number; author: string; deleted?: boolean };

export function titleOf(meta: Pick<NoteMeta, 'title' | 'excerpt'>): string {
  return meta.title.trim() || meta.excerpt.split('\n')[0].replace(/^#+\s*/, '').trim() || 'Sans titre';
}

export class Notes extends EventTarget {
  readonly index = new Map<string, NoteMeta>();
  readonly folders = new Map<string, Folder>();
  readonly #docs = new Map<string, Promise<NoteDoc>>();
  readonly #pending = new Map<string, Uint8Array[]>();
  readonly #saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  #flushTimer: ReturnType<typeof setTimeout> | undefined;
  /** Author clock of the remote update being applied: a received edit keeps its own date. */
  #applyingTs: number | null = null;
  /** Envelope effects (store writes, remote applies), one after another, awaitable. */
  #tasks: Promise<void> = Promise.resolve();

  constructor(
    readonly store: Store,
    readonly journal: Journal,
  ) {
    super();
    journal.addEventListener('envelope', (e) => {
      const detail = (e as CustomEvent).detail;
      this.#tasks = this.#tasks.then(() => this.#onEnvelope(detail)).catch((err) => console.error('note update', err));
    });
  }

  async load(): Promise<void> {
    for (const m of await this.store.allIndex<NoteMeta>()) this.index.set(m.id, m);
    for (const f of (await this.store.getKv<Folder[]>('folders')) ?? []) this.folders.set(f.id, f);
  }

  /** Notes for the list: active (or archived), pinned first, most recently edited first. */
  list({ archived = false } = {}): NoteMeta[] {
    return [...this.index.values()]
      .filter((n) => n.archived === archived)
      .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.edited - a.edited);
  }

  activeFolders(): Folder[] {
    return [...this.folders.values()].filter((f) => !f.deleted).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  }

  open(id: string): Promise<NoteDoc> {
    let doc = this.#docs.get(id);
    if (!doc) {
      doc = (async () => {
        const state = await this.store.getDoc(id);
        const d = state ? new NoteDoc(state) : NoteDoc.fromUpdates((await this.store.updatesFor(id)).map((u) => u.update));
        d.onUpdate((update, remote) => {
          if (!remote) this.#queue(id, update);
          this.#scheduleSave(id, d, this.#applyingTs ?? Date.now());
        });
        return d;
      })();
      this.#docs.set(id, doc);
    }
    return doc;
  }

  async create(fields: Partial<NoteFields> = {}, body = ''): Promise<string> {
    const id = randomHex(16);
    const doc = await this.open(id);
    doc.ydoc.transact(() => {
      doc.set({ created: Date.now(), ...fields });
      if (body) doc.splice(0, 0, body);
    });
    await this.flush();
    return id;
  }

  async update(id: string, fields: Partial<NoteFields>): Promise<void> {
    (await this.open(id)).set(fields);
    await this.flush();
  }

  /**
   * Sends every batched edit now (also called when the page is hidden or the editor closes).
   * Flushes run one after another: a caller always waits for one already in progress, so
   * "flush then read" never misses edits another flush was still writing.
   */
  flush(): Promise<void> {
    const run = this.#flushing.then(() => this.#flushNow());
    this.#flushing = run.catch(() => {});
    return run;
  }

  #flushing: Promise<void> = Promise.resolve();

  async #flushNow(): Promise<void> {
    clearTimeout(this.#flushTimer);
    const batch = [...this.#pending];
    this.#pending.clear();
    for (const [note, updates] of batch) {
      try {
        await this.journal.append({ type: 'note.update', ts: Date.now(), note, update: b64url(NoteDoc.merge(updates)) });
      } catch (e) {
        // A revoked device keeps its local copy but its edits go nowhere: the UI is read-only.
        if (!(e instanceof NotMemberError)) throw e;
        this.dispatchEvent(new Event('readonly'));
      }
    }
    await Promise.all([...this.#saveTimers.keys()].map((id) => this.#saveNow(id)));
    await this.#tasks;
  }

  /** Resolves once every accepted envelope so far has been applied and stored. */
  settled(): Promise<void> {
    return this.#tasks;
  }

  async setFolder(folder: Omit<Folder, 'ts' | 'author'>): Promise<void> {
    const { id, name, color, position, deleted } = folder;
    await this.journal.append({ type: 'folder.set', ts: Date.now(), folder: id, name, color: color ?? null, position, deleted });
  }

  #queue(id: string, update: Uint8Array): void {
    const list = this.#pending.get(id) ?? [];
    list.push(update);
    this.#pending.set(id, list);
    clearTimeout(this.#flushTimer);
    this.#flushTimer = setTimeout(() => void this.flush(), FLUSH_MS);
  }

  #scheduleSave(id: string, doc: NoteDoc, edited: number): void {
    this.#refreshIndex(id, doc, edited);
    clearTimeout(this.#saveTimers.get(id));
    this.#saveTimers.set(id, setTimeout(() => void this.#saveNow(id), SAVE_MS));
  }

  async #saveNow(id: string): Promise<void> {
    clearTimeout(this.#saveTimers.get(id));
    this.#saveTimers.delete(id);
    const doc = await this.#docs.get(id);
    if (!doc) return;
    await this.store.putDoc(id, doc.encode());
    const meta = this.index.get(id);
    if (meta) await this.store.putIndex(id, meta);
  }

  #refreshIndex(id: string, doc: NoteDoc, edited: number): void {
    const text = doc.text();
    const previous = this.index.get(id);
    const meta: NoteMeta = {
      ...doc.fields(),
      id,
      edited: Math.max(edited, previous?.edited ?? 0),
      excerpt: text.slice(0, EXCERPT),
      size: text.length,
    };
    if (!meta.created) meta.created = previous?.created ?? edited;
    this.index.set(id, meta);
    this.dispatchEvent(new CustomEvent('change', { detail: { id } }));
  }

  async #onEnvelope({ envelope, payload, local }: { envelope: { author: string; seq: number }; payload: Payload; local: boolean }) {
    if (payload.type === 'note.update') {
      const update = fromB64url(payload.update);
      await this.store.putUpdate({ note: payload.note, author: envelope.author, seq: envelope.seq, ts: payload.ts, update });
      if (local) return;
      const loaded = this.#docs.get(payload.note);
      if (loaded) {
        const doc = await loaded;
        this.#applyingTs = payload.ts;
        try {
          doc.apply(update);
        } finally {
          this.#applyingTs = null;
        }
      } else {
        // Not open: fold the update into the stored state without keeping the doc in memory.
        const state = await this.store.getDoc(payload.note);
        const doc = state ? new NoteDoc(state) : new NoteDoc();
        doc.apply(update);
        this.#refreshIndex(payload.note, doc, payload.ts);
        await this.store.putDoc(payload.note, doc.encode());
        await this.store.putIndex(payload.note, this.index.get(payload.note));
      }
    } else if (payload.type === 'folder.set') {
      const current = this.folders.get(payload.folder);
      // Last writer wins, ties broken by author id so every device picks the same one.
      if (current && (current.ts > payload.ts || (current.ts === payload.ts && current.author > envelope.author))) return;
      this.folders.set(payload.folder, {
        id: payload.folder,
        name: payload.name,
        color: payload.color,
        position: payload.position,
        deleted: payload.deleted,
        ts: payload.ts,
        author: envelope.author,
      });
      await this.store.setKv('folders', [...this.folders.values()]);
      this.dispatchEvent(new Event('folders'));
    }
  }

  /** Versions of a note, rebuilt from the journal: one entry per batch of edits. */
  async history(id: string): Promise<{ ts: number; author: string; text: string; fields: NoteFields }[]> {
    await this.#tasks;
    const updates = await this.store.updatesFor(id);
    const doc = new NoteDoc();
    return updates.map((u) => {
      doc.apply(u.update);
      return { ts: u.ts, author: u.author, text: doc.text(), fields: doc.fields() };
    });
  }

  /** Restoring is a new edit (non-destructive, as in notd): the old text replaces the current one. */
  async restore(id: string, text: string): Promise<void> {
    const doc = await this.open(id);
    doc.splice(0, doc.text().length, text);
    await this.flush();
  }
}
